import { ColumnId, RowId, SheetColumn } from "./sheetTypes";

/**
 * The view a person has built on top of a sheet: what is filtered, how it is
 * sorted, what is hidden, and whether the first row is a header.
 *
 * Everything here is JSON-serialisable, because the same shape is what gets
 * remembered between openings. Transient UI - the search box, the current
 * selection, the scroll position - deliberately lives outside it.
 */

export type SortDirection = "asc" | "desc";

/** "auto" follows the detected column type; the rest override a bad guess. */
export type SortMode = "auto" | "text" | "number" | "date";

export interface SortRule {
  columnId: ColumnId;
  direction: SortDirection;
  mode: SortMode;
}

export type FilterOperator =
  | "none"
  | "contains"
  | "notContains"
  | "equals"
  | "notEquals"
  | "beginsWith"
  | "endsWith"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  | "isBlank"
  | "isNotBlank";

export interface ColumnCondition {
  operator: FilterOperator;
  value: string;
  /** Second bound, used by "between" only. */
  value2?: string;
}

export interface ColumnFilter {
  columnId: ColumnId;
  /**
   * Chosen values from the checklist, raw cell text; "" stands for (Blanks).
   * null means "the checklist is not restricting anything" - which is not the
   * same as an empty array, and that difference is what makes "uncheck
   * everything" show no rows instead of silently showing all of them.
   */
  values: string[] | null;
  condition: ColumnCondition | null;
}

/** Table or cards; null means "decide by the width of the screen". */
export type SheetViewMode = "table" | "cards";

export interface SheetViewState {
  headerRowIndex: number | null;
  filters: Record<ColumnId, ColumnFilter>;
  sort: SortRule[];
  hiddenColumns: ColumnId[];
  hiddenRows: RowId[];
  /**
   * Every column of the sheet in the order the person put them in, hidden ones
   * included, or null for "the order the file has them in". A full permutation
   * rather than a list of what moved, because a hidden column still needs a
   * place to come back to, and because the grid has to be able to say which
   * columns sit in the gap between two visible ones.
   *
   * It is reconciled against the real columns of the file when the view is
   * compiled, not when it is loaded: the storage key is a path, and the same
   * path can later hold a file with entirely different columns.
   */
  columnOrder: ColumnId[] | null;
  /**
   * Everything visible up to and including this column of the RAW matrix stays
   * put while the rest scrolls. Stored as a source index rather than a count of
   * frozen columns, so hiding a neighbour cannot move the freeze onto a
   * different column. Which columns that covers is read off the CURRENT order,
   * and a move re-anchors it so that the line itself stays where it is.
   */
  frozenThroughSourceIndex: number | null;
  /** Widths set by hand; everything else is measured from the file. */
  columnWidths: Record<ColumnId, number>;
  viewMode: SheetViewMode | null;
}

export interface WorkbookViewState {
  bySheet: Record<string, SheetViewState>;
  activeSheetId: string;
}

export const MAX_SORT_RULES = 3;

export function emptySheetViewState(
  headerRowIndex: number | null,
): SheetViewState {
  return {
    headerRowIndex,
    filters: {},
    sort: [],
    hiddenColumns: [],
    hiddenRows: [],
    columnOrder: null,
    frozenThroughSourceIndex: null,
    columnWidths: {},
    viewMode: null,
  };
}

export function isFilterActive(filter: ColumnFilter | undefined): boolean {
  if (!filter) return false;
  if (filter.values !== null) return true;
  if (filter.condition && filter.condition.operator !== "none") return true;
  return false;
}

export function activeFilterCount(state: SheetViewState): number {
  let count = 0;
  const ids = Object.keys(state.filters);
  for (let i = 0; i < ids.length; i++) {
    if (isFilterActive(state.filters[ids[i]])) count++;
  }
  return count;
}

/**
 * True when these columns are in the order the file has them. A view state
 * stores null rather than an identity permutation, so a drag that ends where it
 * started leaves no trace - no chip, nothing to persist - and the pipeline can
 * go on handing the grid the source's own array.
 */
export function isFileColumnOrder(columns: SheetColumn[]): boolean {
  for (let i = 0; i < columns.length; i++) {
    if (columns[i].sourceIndex !== i) return false;
  }
  return true;
}

/** Has the person changed anything, or are they looking at the file as stored? */
export function isSheetViewModified(
  state: SheetViewState,
  defaultHeaderRowIndex: number | null,
): boolean {
  return (
    state.headerRowIndex !== defaultHeaderRowIndex ||
    activeFilterCount(state) > 0 ||
    state.sort.length > 0 ||
    state.hiddenColumns.length > 0 ||
    state.hiddenRows.length > 0 ||
    state.columnOrder !== null ||
    state.frozenThroughSourceIndex !== null ||
    Object.keys(state.columnWidths).length > 0
  );
}

/** Worth writing to storage: the above, plus a display mode chosen by hand. */
export function isSheetViewWorthPersisting(
  state: SheetViewState,
  defaultHeaderRowIndex: number | null,
): boolean {
  return (
    isSheetViewModified(state, defaultHeaderRowIndex) || state.viewMode !== null
  );
}

/**
 * Fills in fields a stored view may predate. Views saved before freezing,
 * column widths and the card mode existed would otherwise arrive as undefined
 * and take the pipeline down with them.
 */
export function normalizeSheetViewState(
  raw: Partial<SheetViewState> | null | undefined,
): SheetViewState {
  const base = emptySheetViewState(raw?.headerRowIndex ?? null);
  if (!raw) return base;
  return {
    headerRowIndex: raw.headerRowIndex ?? null,
    filters: raw.filters ?? base.filters,
    sort: Array.isArray(raw.sort) ? raw.sort : base.sort,
    hiddenColumns: Array.isArray(raw.hiddenColumns)
      ? raw.hiddenColumns
      : base.hiddenColumns,
    hiddenRows: Array.isArray(raw.hiddenRows)
      ? raw.hiddenRows
      : base.hiddenRows,
    // Ids only. The order is spliced and compared by id, so a number left by an
    // older or hand-edited entry would match no column and quietly exile it to
    // the end of the row - the file order is the honest fallback.
    columnOrder:
      Array.isArray(raw.columnOrder) &&
      raw.columnOrder.every((id) => typeof id === "string")
        ? raw.columnOrder
        : null,
    frozenThroughSourceIndex:
      typeof raw.frozenThroughSourceIndex === "number"
        ? raw.frozenThroughSourceIndex
        : null,
    columnWidths: raw.columnWidths ?? base.columnWidths,
    viewMode:
      raw.viewMode === "table" || raw.viewMode === "cards"
        ? raw.viewMode
        : null,
  };
}

/** What a stored workbook looks like: sheets that may predate current fields. */
export interface StoredWorkbookViewState {
  bySheet?: Record<string, Partial<SheetViewState>>;
  activeSheetId?: string;
}

export function normalizeWorkbookViewState(
  raw: StoredWorkbookViewState | null | undefined,
): WorkbookViewState | null {
  const stored = raw ? raw.bySheet : null;
  if (!stored || typeof stored !== "object") return null;

  const bySheet: Record<string, SheetViewState> = {};
  Object.keys(stored).forEach((sheetId) => {
    bySheet[sheetId] = normalizeSheetViewState(stored[sheetId]);
  });
  return { bySheet, activeSheetId: raw?.activeSheetId ?? "" };
}

export type SheetViewAction =
  | { type: "ensureSheet"; sheetId: string; headerRowIndex: number | null }
  | {
      type: "cycleSort";
      sheetId: string;
      columnId: ColumnId;
      additive: boolean;
    }
  | { type: "setSort"; sheetId: string; rule: SortRule | null }
  | { type: "setSortMode"; sheetId: string; columnId: ColumnId; mode: SortMode }
  | { type: "clearSort"; sheetId: string }
  | {
      type: "setFilter";
      sheetId: string;
      columnId: ColumnId;
      filter: ColumnFilter | null;
    }
  | { type: "clearFilters"; sheetId: string }
  | { type: "hideColumns"; sheetId: string; columnIds: ColumnId[] }
  | { type: "showColumns"; sheetId: string; columnIds: ColumnId[] | "all" }
  | {
      type: "moveColumn";
      sheetId: string;
      /**
       * Every column of the sheet in the order it is shown right now, hidden
       * ones included. The reducer holds ids, so it cannot work an order out
       * for a state whose columnOrder is still null, and it needs sourceIndex
       * to re-anchor the freeze.
       */
      columns: SheetColumn[];
      columnId: ColumnId;
      /** Put it immediately before this one; null drops it past the last. */
      beforeColumnId: ColumnId | null;
      /** Visible columns frozen right now, so the line can stay where it is. */
      frozenCount: number;
    }
  | {
      type: "resetColumnOrder";
      sheetId: string;
      /** The sheet's columns in FILE order. */
      columns: SheetColumn[];
      frozenCount: number;
    }
  | { type: "hideRows"; sheetId: string; rowIds: RowId[] }
  | {
      type: "keepOnlyRows";
      sheetId: string;
      rowIds: RowId[];
      allRowIds: RowId[];
    }
  | { type: "showRows"; sheetId: string; rowIds: RowId[] | "all" }
  | { type: "setHeaderRowIndex"; sheetId: string; index: number | null }
  | {
      type: "setFrozenThrough";
      sheetId: string;
      sourceIndex: number | null;
    }
  | {
      type: "setColumnWidth";
      sheetId: string;
      columnId: ColumnId;
      width: number | null;
    }
  | { type: "setViewMode"; sheetId: string; mode: SheetViewMode | null }
  | { type: "setActiveSheet"; sheetId: string }
  | { type: "resetSheet"; sheetId: string; headerRowIndex: number | null }
  | { type: "hydrate"; state: WorkbookViewState };

function sheetState(
  workbook: WorkbookViewState,
  sheetId: string,
): SheetViewState {
  return workbook.bySheet[sheetId] ?? emptySheetViewState(null);
}

function withSheet(
  workbook: WorkbookViewState,
  sheetId: string,
  next: SheetViewState,
): WorkbookViewState {
  return {
    ...workbook,
    bySheet: { ...workbook.bySheet, [sheetId]: next },
  };
}

function unionIds<T extends string | number>(current: T[], added: T[]): T[] {
  const seen: Record<string, true> = {};
  const out: T[] = [];
  const push = (id: T) => {
    const key = String(id);
    if (seen[key]) return;
    seen[key] = true;
    out.push(id);
  };
  current.forEach(push);
  added.forEach(push);
  return out;
}

function withoutIds<T extends string | number>(
  current: T[],
  removed: T[],
): T[] {
  const drop: Record<string, true> = {};
  removed.forEach((id) => {
    drop[String(id)] = true;
  });
  return current.filter((id) => !drop[String(id)]);
}

/**
 * Moving a column is expressed as "put this one immediately before that one"
 * rather than "at position N": a drop target is what the gesture knows, it does
 * not depend on whether positions count the hidden columns, and a drop that
 * changes nothing comes back as the very same array.
 *
 * The splice happens in the FULL order, so a hidden column that sat to the left
 * of the drop target stays to its left, and the visible result is exactly what
 * the drop indicator promised.
 */
function movedColumns(
  columns: SheetColumn[],
  columnId: ColumnId,
  beforeColumnId: ColumnId | null,
): SheetColumn[] {
  const from = columns.findIndex((column) => column.id === columnId);
  if (from < 0) return columns;
  const moved = columns[from];
  const rest = columns.slice(0, from).concat(columns.slice(from + 1));
  if (beforeColumnId == null) return rest.concat([moved]);
  const to = rest.findIndex((column) => column.id === beforeColumnId);
  if (to < 0) return columns;
  return rest.slice(0, to).concat([moved], rest.slice(to));
}

function sameColumnOrder(a: ColumnId[] | null, b: ColumnId[] | null): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * Where the freeze boundary ends up once the columns have moved. Freeze panes
 * hold their position: freeze two columns, drag a third in front of them, and
 * two columns are still frozen - whichever two now sit there. So the boundary
 * is re-anchored onto the column that has taken the last frozen place. Doing it
 * inside the same action is what makes one undo put the order and the freeze
 * back together.
 */
function refrozenThrough(
  current: SheetViewState,
  ordered: SheetColumn[],
  frozenCount: number,
): number | null {
  if (frozenCount <= 0) return current.frozenThroughSourceIndex;
  const hidden: Record<string, true> = {};
  current.hiddenColumns.forEach((id) => {
    hidden[id] = true;
  });
  const visible = ordered.filter((column) => !hidden[column.id]);
  const boundary = visible[Math.min(frozenCount, visible.length) - 1];
  return boundary ? boundary.sourceIndex : current.frozenThroughSourceIndex;
}

/**
 * Sorting cycles asc -> desc -> off, the same three states the rest of the
 * platform uses (see utils/sortUtils.cycleSortField). Shift-clicking appends a
 * column instead of replacing the sort, which is how "department, then name"
 * gets expressed.
 */
function cycleSort(
  sort: SortRule[],
  columnId: ColumnId,
  additive: boolean,
): SortRule[] {
  const existing = sort.find((rule) => rule.columnId === columnId);
  const mode: SortMode = existing ? existing.mode : "auto";

  if (!additive) {
    if (!existing || sort.length > 1) {
      return [{ columnId, direction: "asc", mode }];
    }
    if (existing.direction === "asc") {
      return [{ columnId, direction: "desc", mode }];
    }
    return [];
  }

  if (!existing) {
    if (sort.length >= MAX_SORT_RULES) return sort;
    return sort.concat([{ columnId, direction: "asc", mode }]);
  }
  if (existing.direction === "asc") {
    return sort.map((rule) =>
      rule.columnId === columnId
        ? { ...rule, direction: "desc" as const }
        : rule,
    );
  }
  return sort.filter((rule) => rule.columnId !== columnId);
}

export function workbookViewReducer(
  workbook: WorkbookViewState,
  action: SheetViewAction,
): WorkbookViewState {
  switch (action.type) {
    case "hydrate":
      return action.state;

    case "setActiveSheet":
      return { ...workbook, activeSheetId: action.sheetId };

    case "ensureSheet":
      if (workbook.bySheet[action.sheetId]) return workbook;
      return withSheet(
        workbook,
        action.sheetId,
        emptySheetViewState(action.headerRowIndex),
      );

    case "cycleSort": {
      const current = sheetState(workbook, action.sheetId);
      return withSheet(workbook, action.sheetId, {
        ...current,
        sort: cycleSort(current.sort, action.columnId, action.additive),
      });
    }

    case "setSort": {
      const current = sheetState(workbook, action.sheetId);
      return withSheet(workbook, action.sheetId, {
        ...current,
        sort: action.rule ? [action.rule] : [],
      });
    }

    case "setSortMode": {
      const current = sheetState(workbook, action.sheetId);
      return withSheet(workbook, action.sheetId, {
        ...current,
        sort: current.sort.map((rule) =>
          rule.columnId === action.columnId
            ? { ...rule, mode: action.mode }
            : rule,
        ),
      });
    }

    case "clearSort": {
      const current = sheetState(workbook, action.sheetId);
      if (current.sort.length === 0) return workbook;
      return withSheet(workbook, action.sheetId, { ...current, sort: [] });
    }

    case "setFilter": {
      const current = sheetState(workbook, action.sheetId);
      const filters = { ...current.filters };
      if (action.filter === null || !isFilterActive(action.filter)) {
        delete filters[action.columnId];
      } else {
        filters[action.columnId] = action.filter;
      }
      return withSheet(workbook, action.sheetId, { ...current, filters });
    }

    case "clearFilters": {
      const current = sheetState(workbook, action.sheetId);
      if (Object.keys(current.filters).length === 0) return workbook;
      return withSheet(workbook, action.sheetId, { ...current, filters: {} });
    }

    case "hideColumns": {
      const current = sheetState(workbook, action.sheetId);
      if (action.columnIds.length === 0) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        hiddenColumns: unionIds(current.hiddenColumns, action.columnIds),
      });
    }

    case "showColumns": {
      const current = sheetState(workbook, action.sheetId);
      if (current.hiddenColumns.length === 0) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        hiddenColumns:
          action.columnIds === "all"
            ? []
            : withoutIds(current.hiddenColumns, action.columnIds),
      });
    }

    case "moveColumn": {
      const current = sheetState(workbook, action.sheetId);
      const next = movedColumns(
        action.columns,
        action.columnId,
        action.beforeColumnId,
      );
      const columnOrder = isFileColumnOrder(next)
        ? null
        : next.map((column) => column.id);
      const frozenThroughSourceIndex = refrozenThrough(
        current,
        next,
        action.frozenCount,
      );
      if (
        sameColumnOrder(current.columnOrder, columnOrder) &&
        current.frozenThroughSourceIndex === frozenThroughSourceIndex
      ) {
        return workbook;
      }
      return withSheet(workbook, action.sheetId, {
        ...current,
        columnOrder,
        frozenThroughSourceIndex,
      });
    }

    case "resetColumnOrder": {
      const current = sheetState(workbook, action.sheetId);
      if (current.columnOrder === null) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        columnOrder: null,
        // Putting the columns back is a move like any other, so the freeze line
        // stays where it is rather than following a column across the sheet.
        frozenThroughSourceIndex: refrozenThrough(
          current,
          action.columns,
          action.frozenCount,
        ),
      });
    }

    case "hideRows": {
      const current = sheetState(workbook, action.sheetId);
      if (action.rowIds.length === 0) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        hiddenRows: unionIds(current.hiddenRows, action.rowIds),
      });
    }

    case "keepOnlyRows": {
      const current = sheetState(workbook, action.sheetId);
      const keep: Record<string, true> = {};
      action.rowIds.forEach((id) => {
        keep[String(id)] = true;
      });
      const dropped = action.allRowIds.filter((id) => !keep[String(id)]);
      return withSheet(workbook, action.sheetId, {
        ...current,
        hiddenRows: unionIds(current.hiddenRows, dropped),
      });
    }

    case "showRows": {
      const current = sheetState(workbook, action.sheetId);
      if (current.hiddenRows.length === 0) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        hiddenRows:
          action.rowIds === "all"
            ? []
            : withoutIds(current.hiddenRows, action.rowIds),
      });
    }

    case "setFrozenThrough": {
      const current = sheetState(workbook, action.sheetId);
      if (current.frozenThroughSourceIndex === action.sourceIndex) {
        return workbook;
      }
      return withSheet(workbook, action.sheetId, {
        ...current,
        frozenThroughSourceIndex: action.sourceIndex,
      });
    }

    case "setColumnWidth": {
      const current = sheetState(workbook, action.sheetId);
      const existing = current.columnWidths[action.columnId];
      if (action.width === null && existing == null) return workbook;
      if (action.width !== null && existing === action.width) return workbook;
      const columnWidths = { ...current.columnWidths };
      if (action.width === null) {
        delete columnWidths[action.columnId];
      } else {
        columnWidths[action.columnId] = action.width;
      }
      return withSheet(workbook, action.sheetId, { ...current, columnWidths });
    }

    case "setViewMode": {
      const current = sheetState(workbook, action.sheetId);
      if (current.viewMode === action.mode) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        viewMode: action.mode,
      });
    }

    case "setHeaderRowIndex": {
      const current = sheetState(workbook, action.sheetId);
      if (current.headerRowIndex === action.index) return workbook;
      return withSheet(workbook, action.sheetId, {
        ...current,
        headerRowIndex: action.index,
      });
    }

    case "resetSheet": {
      const current = sheetState(workbook, action.sheetId);
      return withSheet(workbook, action.sheetId, {
        ...emptySheetViewState(action.headerRowIndex),
        viewMode: current.viewMode,
      });
    }

    default:
      return workbook;
  }
}
